import type { SxProps } from '@mui/material';
import { Button, CircularProgress, Typography } from '@mui/material';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { ConfirmationDialog, PlaceholderNoAccess, SuspenseBoundary, TextField } from 'components/common';
import type { CreativeTextDetails, Flow } from '../types';
import { isEmpty } from 'lodash-es';
import type { CreativeTextPayload, CreativeTextResponse } from '../api';
import { useAddCreativeText, useFlows, useUpdateCreativeText } from '../api';
import {
  cabinetDatadogSources,
  createCabinetMutationTracking,
  trackCabinetSubmitAttempt,
} from 'lib/datadog';
import SelectLanguage from './SelectLanguage';
import AddCreativeTextLocalizations from './AddCreativeTextLocalizations';
import { FormControlContainer } from './CreativeTextsComponents';
import { addCreativeTextFormValidator } from '../validation';
import type { z } from 'zod';
import DeleteCreativeText from './DeleteCreativeText';
import { useEnqueueSnackbar, useModal, usePagination, useScrollToError } from 'hooks';
import Backdrop from '@mui/material/Backdrop';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router';
import type { ListResponse } from 'types/shared';
import SelectCreativeTextFlows from './SelectCreativeTextFlows';
import { usePermissionGuard } from 'features/login';

const EditAdditionalFlowsComponent = () => {
  const [editFlow, setEditFlow] = useState(false);
  const pagination = usePagination([], 100);

  const { watch } = useFormContext();
  const fieldAdditionalFlows = watch('additional_flows_ids') as number[];
  const isEmptyAdditionalFlows = isEmpty(fieldAdditionalFlows);

  const { data: flowsList } = useFlows({
    params: {
      limit: pagination.perPage,
      offset: pagination.page ?? 1,
    },
    config: {
      select: (response: ListResponse<Flow>): Flow[] => {
        return response.data.filter((flow: Flow) =>
          fieldAdditionalFlows.includes(flow.id),
        );
      },
    },
  });

  const handleEditFlow = () => {
    setEditFlow((prev) => !prev);
  };
  return editFlow ? (
    <>
      <Backdrop
        onClick={handleEditFlow}
        open={editFlow}
        sx={(theme) => ({
          backgroundColor: 'transparent',
          zIndex: theme.zIndex.drawer + 1,
        })}
      ></Backdrop>
      <SelectCreativeTextFlows
        multiples
        name={'additional_flows_ids'}
        onClose={handleEditFlow}
        open={editFlow}
      />
    </>
  ) : (
    <Box
      sx={(t) => ({
        display: 'flex',
        width: '100%',
        flexDirection: 'row',
        border: `1px solid ${t.palette.divider}`,
        borderRadius: t.borderRadius.md,
        p: 1.5,
      })}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {flowsList.map((flow: Flow) => (
          <Typography key={flow.id} variant={'body2'}>
            {flow.name}
          </Typography>
        ))}
        {isEmptyAdditionalFlows && (
          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
            <Typography variant={'body2'} color={'text.disabled'}>
              Flows
            </Typography>
          </Box>
        )}
      </Box>
      <Box>
        <Button
          onClick={handleEditFlow}
          color={'inherit'}
          size={'small'}
          startIcon={<EditIcon />}
        >
          Edit flow
        </Button>
      </Box>
    </Box>
  );
};

const EditAdditionalFlows = () => {
  const { accessToViewFlow } = usePermissionGuard();
  if (!accessToViewFlow) return <PlaceholderNoAccess title={'No access to view flow'} />;

  return <EditAdditionalFlowsComponent />;
};

interface CreativeTextFormProps {
  creativeText?: CreativeTextDetails;
  titleForm?: ReactNode;
  onSuccess?: (data: CreativeTextResponse) => void;
  onUpdateSuccess?: (data: CreativeTextResponse) => void;
  autoAddLocalizedVersion?: boolean;
  sx?: SxProps;
}

type FormData = z.infer<typeof addCreativeTextFormValidator.schema>;

const CreativeTextForm: FC<CreativeTextFormProps> =
  ({
     creativeText,
     titleForm,
     onSuccess,
     onUpdateSuccess,
     autoAddLocalizedVersion = false,
     sx,
   }) => {
    const { accessToDeleteOriginaltextcreative } = usePermissionGuard();
    const { onShowErrorAlert, onShowAlert } = useEnqueueSnackbar();
    const navigate = useNavigate();
    const { open, close, isOpen } = useModal();
    const isEditMode = !isEmpty(creativeText);

    const addCreativeText = useAddCreativeText({
      config: createCabinetMutationTracking<CreativeTextResponse>({
        platform: 'facebook',
        entity: 'creative_text',
        mode: 'create',
        onSuccess: (data: CreativeTextResponse) => {
          onShowAlert('Creative text added successfully');
          if (onSuccess) {
            onSuccess(data);
          } else {
            navigate('/cabinet/facebook/creative-texts');
          }
        },
        onError: (error) => {
          onShowErrorAlert(error.message);
        },
      }),
    });

    const updateCreativeText = useUpdateCreativeText({
      config: createCabinetMutationTracking({
        platform: 'facebook',
        entity: 'creative_text',
        mode: 'edit',
        onSuccess: (data: CreativeTextResponse) => {
          onShowAlert('Creative text updated successfully');
          if (onUpdateSuccess) {
            onUpdateSuccess(data);
          } else {
            navigate('/cabinet/facebook/creative-texts');
          }
        },
        onError: (error) => {
          onShowErrorAlert(error.message);
        },
      }),
    });

    const formMethods = useForm<FormData>({
      resolver: addCreativeTextFormValidator.resolver,
      defaultValues: {
        title: creativeText?.title ?? '',
        body: creativeText?.body ?? '',
        language: creativeText?.language ?? '',
        tag: creativeText?.tag ?? '',
        description: creativeText?.description ?? '',

        default_flows_ids: creativeText?.default_flows
          ? creativeText.default_flows.map((flow: Flow) => flow.id)
          : [],

        additional_flows_ids: creativeText?.additional_flows
          ? creativeText.additional_flows.map((flow: Flow) => flow.id)
          : [],

        localizations: creativeText?.localizations
          ? creativeText.localizations?.map(
            ({ title, body, language, description }) => ({
              title,
              body,
              language,
              description,
            }),
          )
          : [],
      },
    });

    const {
      handleSubmit,
      formState: { errors },
    } = formMethods;

    useScrollToError<FormData>({ errors });

    const onSubmit = (data: FormData) => {
      trackCabinetSubmitAttempt(
        'facebook',
        'creative_text',
        isEditMode ? 'edit' : 'create',
      );

      const payload: CreativeTextPayload = {
        title: data.title,
        body: data.body,
        language: data.language,
        tag: data.tag,
        description: data.description,
        additional_flows_ids: data.additional_flows_ids,
        default_flows_ids: data.default_flows_ids,
        localizations: data.localizations,
      };

      if (isEditMode && creativeText) {
        updateCreativeText.mutate({ ...payload, id: creativeText.id });
      } else {
        addCreativeText.mutate(payload);
      }
    };

    const isPending = isEditMode
      ? updateCreativeText.isPending
      : addCreativeText.isPending;

    return (
      <>
        <FormProvider {...formMethods}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              pr: 2,
              alignItems: 'start',
              justifyContent: 'start',
              ...sx,
            }}
          >
            {titleForm ?? (
              <Typography variant={'subtitle1'}>
                {isEditMode ? 'Edit text' : 'Add text'}
              </Typography>
            )}
            <FormControlContainer title={'Title'}>
              <TextField
                name={'title'}
                placeholder={'Value'}
                inputProps={{ autoFocus: true }}
              />
            </FormControlContainer>
            <FormControlContainer title={'Body'}>
              <TextField
                name={'body'}
                placeholder={'Value'}
                multiline
                inputProps={{
                  sx: {
                    '&.MuiInputBase-input': {
                      minHeight: '150px',
                      borderRadius: 0,
                    },
                  },
                }}
              />
            </FormControlContainer>
            <FormControlContainer title={'Details'}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                  gap: 4,
                  width: '100%',
                  maxWidth: '400px',
                }}
              >
                <SelectLanguage />
                <TextField
                  name={'tag'}
                  inputLabel={'Tag'}
                  placeholder={'Value'}
                />
              </Box>
            </FormControlContainer>
            <FormControlContainer title={'Flows where text can be used!'}>
              <SuspenseBoundary
                datadogSource={cabinetDatadogSources.query('facebook', 'flow')}
              >
                {isEditMode ? (
                  <EditAdditionalFlows />
                ) : (
                  <SelectCreativeTextFlows
                    multiples
                    name={'additional_flows_ids'}
                    creativeText={creativeText}
                  />
                )}
              </SuspenseBoundary>
            </FormControlContainer>
            <FormControlContainer title={'Description (optional)'}>
              <TextField
                name={'description'}
                placeholder={'Value'}
                multiline
                inputProps={{
                  sx: {
                    '&.MuiInputBase-input': {
                      minHeight: '150px',
                      borderRadius: 0,
                    },
                  },
                }}
              />
            </FormControlContainer>
            <AddCreativeTextLocalizations autoAddOnMount={autoAddLocalizedVersion} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Button
                startIcon={
                  isPending && <CircularProgress color={'inherit'} size={18} />
                }
                onClick={handleSubmit(onSubmit)}
                variant={'contained'}
                disabled={isPending}
              >
                {isEditMode ? 'Update text' : 'Save text'}
              </Button>
              {isEditMode && creativeText && (
                <>
                  <Button
                    variant={'outlined'}
                    color={'error'}
                    onClick={open}
                    disabled={isPending || !accessToDeleteOriginaltextcreative}
                  >
                    Delete
                  </Button>
                  {accessToDeleteOriginaltextcreative && <DeleteCreativeText
                    isOpen={isOpen}
                    close={close}
                    id={creativeText.id}
                    name={creativeText.title}
                    open={open}
                  />}
                </>
              )}
            </Box>
          </Box>
          <ConfirmationDialog isUnblock={addCreativeText.isSuccess || updateCreativeText.isSuccess} />
        </FormProvider>
      </>
    );
  };

export default CreativeTextForm;
